<script lang="ts" setup>
import type { GreetingCardStorySlide } from '@consumer/types/serializers'
import GreetingCardNote from '@consumer/modules/recipientGiftLandingV2/components/OpenGift/GreetingCardNote.vue'
import { useRecipientLanding } from '@consumer/modules/recipientGiftLandingV2/composables'
import GreetingCardDemo from './GreetingCardDemo.vue'
import GreetingCardVoiceMemo from './GreetingCardVoiceMemo.vue'

const { gift } = useRecipientLanding()

const greetingCardRef = ref<HTMLElement>()

let isOpen = $ref(false)
let showingPhotos = $ref(false)
let showingVoiceMemo = $ref(false)
let currentSlideIndex = $ref(0)

const storySlides = $computed<GreetingCardStorySlide[]>(
  () => gift.value.greeting?.greetingCardCustomization?.storySlides ?? [],
)

const currentSlide = $computed(() => storySlides[currentSlideIndex])

function photoUrl (slide: GreetingCardStorySlide) {
  if (slide.base64Photo) return slide.base64Photo
  if (typeof slide.photo === 'object' && slide.photo?.url) return slide.photo.url
  if (typeof slide.photo === 'string') return slide.photo
  return ''
}

type OpenOptions = { showPhoto?: number; showVoiceMemo?: boolean }
const open = ({ showPhoto, showVoiceMemo: voiceMemo }: OpenOptions = {}) => new Promise<void>((resolve) => {
  isOpen = true
  showingPhotos = showPhoto !== undefined
  showingVoiceMemo = voiceMemo ?? false
  currentSlideIndex = showPhoto ?? 0
  if (greetingCardRef.value) {
    greetingCardRef.value.classList.add('open')
  }
  resolve()
})

const close = () => new Promise<void>((resolve) => {
  isOpen = false
  if (greetingCardRef.value) {
    greetingCardRef.value.classList.remove('open')
  }
  resolve()
})

const showPhotos = (index = 0) => {
  currentSlideIndex = index
  showingPhotos = true
  showingVoiceMemo = false
}

const hidePhotos = () => {
  showingPhotos = false
  currentSlideIndex = 0
}

const toggle = () => new Promise<void>(() => {
  isOpen = !isOpen
  if (greetingCardRef.value) {
    greetingCardRef.value.classList.toggle('open')
  }
})

defineExpose({
  open,
  close,
  toggle,
  showPhotos,
  hidePhotos,
})
</script>

<template>
  <div
    ref="greetingCardRef"
    class="greeting-card-wrapper flex justify-center"
    style="height: 360px; width: 370px;"
  >
    <div class="relative" style="height: 360px; width: 270px;">

      <div class="greeting-card-container absolute inset-0">
        <div class="greeting-card w-full h-full">
          <div class="greeting-card-inner">
            <div class="greeting-card-front">
              <img
                v-if="gift?.greeting?.card?.previewImageUrl"
                :src="gift?.greeting?.card?.previewImageUrl"
                class="rounded-v2md shadow-v2lg"
              >
              <GreetingCardDemo v-else class="rounded-v2md shadow-v2lg"/>
            </div>
            <div class="greeting-card-back bg-white shadow-v2lg rounded-l-v2sm"/>
          </div>
        </div>
      </div>
      <div
        class="greeting-card-note-container absolute inset-0 transition-all duration-500"
        :class="isOpen ? 'z-2' : '-z-1'"
      >
        <div
          v-if="showingVoiceMemo"
          class="voice-memo-container shadow-v2lg"
          :class="isOpen ? 'rounded-r-v2md' : 'rounded-v2md'"
        >
          <GreetingCardVoiceMemo/>
        </div>
        <div
          v-else-if="showingPhotos && currentSlide"
          :key="`photo-${currentSlideIndex}`"
          class="photo-slide shadow-v2lg"
          :class="isOpen ? 'rounded-r-v2md' : 'rounded-v2md'"
        >
          <img
            :src="photoUrl(currentSlide)"
            class="photo-image"
            :class="isOpen ? 'rounded-tr-v2md' : 'rounded-t-v2md'"
          >
          <div v-if="currentSlide.text" class="photo-caption">
            <p>
              {{ currentSlide.text }}
            </p>
          </div>
        </div>
        <GreetingCardNote
          v-else
          class="shadow-v2lg"
          :class="isOpen ? 'rounded-r-v2md' : 'rounded-v2md'"
        />
      </div>
    </div>
  </div>
</template>

<style lang="css">
.greeting-card {
  perspective: 1000px;
}

.greeting-card-inner {
  height: 100%;
  position: relative;
  text-align: center;
  transform: rotateY(0deg);
  transform-origin: left;
  transform-style: preserve-3d;
  transition: transform 0.6s;
  width: 100%;
}

.open .greeting-card .greeting-card-inner {
  transform: rotateY(-180deg) skewY(24deg) scaleX(0.17);
}

.greeting-card-front,
.greeting-card-back {
  backface-visibility: hidden;
  height: 100%;
  position: absolute;
  width: 100%;
}

.greeting-card-back {
  transform: rotateY(-180deg);
}
</style>

<style scoped lang="scss">
.voice-memo-container {
  height: 100%;
  width: 100%;
}

.photo-slide {
  background: white;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
  width: 100%;
}

.photo-image {
  display: block;
  flex-shrink: 0;
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.photo-caption {
  align-items: center;
  bottom: 0;
  display: flex;
  flex: 1;
  justify-content: center;
  left: 0;
  padding: 24px;
  position: absolute;
  width: 100%;

  > p {
    background: white;
    border-radius: 10px;
    color: black;
    font-family: Gilroy, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.4;
    padding: 10px;
    text-align: center;
  }
}

.note-fade-enter-active,
.note-fade-leave-active {
  transition: opacity 0.25s ease;
}

.note-fade-enter-from,
.note-fade-leave-to {
  opacity: 0;
}
</style>
